import { experienceList } from "@/constants/career-form";
import { contactUsPositionList } from "@/constants/contact-us-form";
import {
  batchesList,
  courseEnrollmentPositionList,
} from "@/constants/course-enrollment-form";
import { referenceList } from "@/constants/partners-form";
import { linkedInUrlRegex } from "@/utils/regex.helper";
import { array, boolean, mixed, object, string } from "yup";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { securityRequestAccessRequestsList } from "@/constants/security-request-access-form";
import { IFormField } from "@/api/dtos/shared-component.type";
import { trimEnd } from "lodash";

export type ICommonList = {
  value: string;
  label: string;
};

export type ISelectionSchema = {
  selectionList: ICommonList[];
};

export type ISpecificSelectionSchema = {
  selectionField: string;
};

const isValidPhoneNumber = (value?: string) => {
  if (!value) return false;

  const phoneNumber = parsePhoneNumberFromString(value);

  if (phoneNumber && phoneNumber.nationalNumber.length === 0) {
    return true;
  }

  return phoneNumber ? phoneNumber.isValid() : false;
};

const nameValidation = string().trim().required().max(255);
const emailValidation = string().required().email().max(255);
const phoneValidation = string().test(
  "isValidPhoneNumber",
  "Please enter a valid phone number",
  (value) => isValidPhoneNumber(value),
);
const linkedInUrlValidation = string()
  .required()
  .matches(linkedInUrlRegex, "Invalid LinkedIn URL format")
  .label("LinkedIn profile");
const descriptionValidation = string().trim().required().max(500);
const ctcValidation = string()
  .required()
  .test(
    "typeError",
    ({ label }) => `${label} must be number`,
    (val) => /^(\d+(\.\d*)?|\.\d+)$/.test(val),
  )
  .test(
    "typeError",
    ({ label }) => `${label} must be 2 digit cent`,
    (val) => /^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(val),
  );
const countryStateCityValidation = string()
  .required()
  .nullable()
  .test(
    "stateRequired",
    ({ label }) => `Please select at least one ${label.toLowerCase()}`,
    (value) => value !== null,
  );
const concentValidation = boolean();

// Selection type validation
const selectionListValidation = ({ selectionList }: ISelectionSchema) => {
  return string()
    .required()
    .nullable()
    .test(
      "required",
      ({ label }) => `Please select at least one ${label.toLowerCase()}`,
      (value) => value !== null,
    )
    .oneOf(
      selectionList.map((selected) => selected.value),
      ({ label }) => `${label} is not valid`,
    );
};
const specificSelectionListValidation = ({
  selectionField,
}: ISpecificSelectionSchema) => {
  return string()
    .trim()
    .when(selectionField, {
      is: (selected: string) => selected === "Other",
      then: (string) => string.required(),
    })
    .max(255);
};

const generateLabel = (defaultLabel: string, label?: string) => {
  return label ? trimEnd(label, "*").trim() : defaultLabel;
};

export const customFormSchema = (fields: IFormField[]) => {
  return object().shape({
    firstName: string()
      .trim()
      .when([], {
        is: () => fields.some(({ field }) => field === "first_name"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .max(255)
      .label(
        generateLabel(
          "First Name",
          fields.find(({ field }) => field === "first_name")?.placeholder,
        ),
      ),
    lastName: string()
      .trim()
      .when([], {
        is: () => fields.some(({ field }) => field === "last_name"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .max(255)
      .label(
        generateLabel(
          "Last Name",
          fields.find(({ field }) => field === "last_name")?.placeholder,
        ),
      ),
    email: string()
      .email()
      .when([], {
        is: () => fields.some(({ field }) => field === "email"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .label(
        generateLabel(
          "Email",
          fields.find(({ field }) => field === "email")?.placeholder,
        ),
      ),
    workEmail: string()
      .email()
      .when([], {
        is: () => fields.some(({ field }) => field === "work_email"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .label(
        generateLabel(
          "Work Email",
          fields.find(({ field }) => field === "work_email")?.placeholder,
        ),
      ),
    position: string()
      .nullable()
      .when([], {
        is: () => fields.some(({ field }) => field === "position"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .oneOf(
        fields
          .find(({ field }) => field === "position")
          ?.selection_data?.map((position) => position.key) ?? [],
        `${generateLabel(
          "Position",
          fields.find(({ field }) => field === "position")?.placeholder,
        )} is not valid`,
      )
      .label(
        generateLabel(
          "Position",
          fields.find(({ field }) => field === "position")?.placeholder,
        ),
      ),
    specificPosition: string()
      .trim()
      .when("position", {
        is: (position: string) => position === "Other",
        then: (string) => string.required(),
      })
      .max(255)
      .label(
        generateLabel(
          "Specific Position",
          fields.find(({ field }) => field === "position")?.placeholder,
        ),
      ),
    batch: string()
      .nullable()
      .when([], {
        is: () => fields.some(({ field }) => field === "batch"),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      })
      .oneOf(
        fields
          .find(({ field }) => field === "batch")
          ?.selection_data?.map((batch) => batch.key) ?? [],
        `${generateLabel(
          "Batch",
          fields.find(({ field }) => field === "batch")?.placeholder,
        )} is not valid`,
      )
      .label(
        generateLabel(
          "Batch",
          fields.find(({ field }) => field === "batch")?.placeholder,
        ),
      ),
    phone: string().test(
      "isValidPhoneNumber",
      "Please enter a valid phone number",
      (value) =>
        fields.some(({ field }) => field === "batch")
          ? isValidPhoneNumber(value)
          : true,
    ),
    consents: array().of(boolean().required()),
  });
};

export const courseEnrollmentSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  email: emailValidation.label("Email"),
  position: selectionListValidation({
    selectionList: courseEnrollmentPositionList,
  }).label("Position"),
  specificPosition: specificSelectionListValidation({
    selectionField: "position",
  }).label("Specific Position"),
  batch: selectionListValidation({
    selectionList: batchesList,
  }).label("Batch"),
  phone: phoneValidation,
  emailConcent: concentValidation,
  phoneConcent: concentValidation,
  laptopConcent: concentValidation,
});

export const partnersSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  companyName: nameValidation.label("Company Name"),
  companyLinkedin: linkedInUrlValidation,
  companyEmail: emailValidation.label("Company email"),
  reference: selectionListValidation({
    selectionList: referenceList,
  }).label("Reference"),
  specificReference: specificSelectionListValidation({
    selectionField: "reference",
  }).label("Specific reference"),
  description: descriptionValidation.label("Description"),
  emailConcent: concentValidation,
});

export const contactUsSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  companyName: nameValidation.label("Organization name"),
  workEmail: emailValidation.label("Work email"),
  position: selectionListValidation({
    selectionList: contactUsPositionList,
  }).label("Position"),
  specificPosition: specificSelectionListValidation({
    selectionField: "position",
  }).label("Specific position"),
  description: descriptionValidation.label("Description"),
  phone: phoneValidation,
  emailConcent: concentValidation,
});

export const careerSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  email: emailValidation.label("Email"),
  phone: phoneValidation,
  position: nameValidation.label("Position"),
  experience: selectionListValidation({
    selectionList: experienceList,
  }).label("Experience"),
  currentCTC: ctcValidation.label("Current CTC"),
  expectedCTC: ctcValidation.label("Expected CTC"),
  state: countryStateCityValidation.label("State"),
  city: countryStateCityValidation.label("City"),
  resume: mixed<File>()
    .required()
    .test("isFile", "Resume is a required field", (file) => file !== null)
    .test(
      "fileSize",
      "The file must be less than 5 MB",
      (file) => !!file && file.size <= 5 * 1024 * 1024,
    )
    .test(
      "fileType",
      "Only JPEG, JPG and PDF files are allowed",
      (file) =>
        !!file &&
        (file.type === "application/pdf" || file.type === "image/jpeg"),
    )
    .nullable()
    .label("Resume"),
  emailConcent: concentValidation,
});

export const ResourcesDownloadSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  email: emailValidation.label("Email"),
  country: countryStateCityValidation.label("Country"),
  emailConcent: concentValidation,
});

// Newsletter signup — first + last name + work email all required. The API
// concatenates `${firstName} ${lastName}` as the `name` value passed to
// downstream Sendy + Google Sheets flows (both expect a single name string).
export const newsLetterSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  email: emailValidation.label("Work email"),
});

// Low-friction lead-capture form (pentest / VAPT landing pages). Deliberately
// short — full name, work email, company, and a scope hint — so the form can
// sit above the fold and convert paid traffic without a page nav. `intent`
// ("quote" | "sample-report") drives the downstream event + sheet column.
export const leadCaptureSchema = object().shape({
  name: nameValidation.label("Name"),
  workEmail: emailValidation.label("Work email"),
  company: nameValidation.label("Company"),
  scope: string().trim().required().max(120).label("Scope"),
  intent: string().oneOf(["quote", "sample-report"]).required().label("Intent"),
  emailConcent: boolean(),
});

export const securityRequestAccessSchema = object().shape({
  firstName: nameValidation.label("First name"),
  lastName: nameValidation.label("Last name"),
  email: emailValidation.label("Business email"),
  companyName: nameValidation.label("Company name"),
  phone: phoneValidation,
  requestFor: nameValidation.label("Request for"),
  specificRequestFor: specificSelectionListValidation({
    selectionField: "requestFor",
  }).label("Specific Request"),
  reason: descriptionValidation.label("Reason"),
  emailConcent: concentValidation,
});
